import React, { useEffect, useRef, useState } from "react";
import { useHits, useInstantSearch } from "react-instantsearch-hooks";
import { useDispatch } from "react-redux";
import Loader from "@/components/Loader.jsx";
import Badge from "@/components/Badges.jsx";
import { addToast } from "../../store/app/action.js";
import { patchAdminProductUpc, searchUpcDatabase } from "../../api/masterMenu.js";
import MasterMenuEditProduct from "./MasterMenuEditProduct.jsx";

const initialLookup = {
  activeSku: null,
  loading: false,
  items: [],
  retailerFeedUpcs: [],
  error: null,
  message: null,
  openUpward: false,
};

function makeUpcSearchCacheKey({ title, subtitle, sku_id, product_id, page }) {
  return JSON.stringify({
    title: String(title || "").trim(),
    subtitle: String(subtitle || "").trim(),
    sku_id: String(sku_id || "").trim(),
    product_id: String(product_id || "").trim(),
    page: page || 1,
  });
}

function messageFromUpcSearchPayload(data) {
  return data.success === false ||
    (!(data.suggestions && data.suggestions.length) &&
      !(data.retailer_feed_upcs && data.retailer_feed_upcs.length))
    ? data.message || "No close match found."
    : null;
}

const MasterMenuHits = ({ headers, missingUpcMode = false }) => {
  const { hits, status } = useHits();
  const { refresh } = useInstantSearch();
  const dispatch = useDispatch();
  const [drafts, setDrafts] = useState({});
  const [savingSku, setSavingSku] = useState(null);
  const [upcLookup, setUpcLookup] = useState(initialLookup);
  const [editSku, setEditSku] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const blurCloseTimer = useRef(null);
  const inputRefsBySku = useRef({});
  const upcSearchCacheRef = useRef(new Map());

  useEffect(() => {
    return () => {
      if (blurCloseTimer.current) {
        clearTimeout(blurCloseTimer.current);
      }
    };
  }, []);

  const clearBlurTimer = () => {
    if (blurCloseTimer.current) {
      clearTimeout(blurCloseTimer.current);
      blurCloseTimer.current = null;
    }
  };

  const scheduleCloseLookup = () => {
    clearBlurTimer();
    blurCloseTimer.current = setTimeout(() => {
      setUpcLookup(initialLookup);
    }, 180);
  };

  const openUpcLookup = async (hit) => {
    if (!missingUpcMode) return;
    clearBlurTimer();
    const inputEl = inputRefsBySku.current[hit.sku_id];
    const inputRect = inputEl ? inputEl.getBoundingClientRect() : null;
    const estimatedDropdownHeight = 260;
    const viewportBottom = window.innerHeight || 0;
    const hasRoomBelow = inputRect
      ? viewportBottom - inputRect.bottom > estimatedDropdownHeight
      : true;
    const openUpward = !hasRoomBelow;
    const title = String(hit.title || "").trim();
    const subtitle = String(hit.subtitle || "").trim();
    const combined = [title, subtitle].filter(Boolean).join(" ").trim();
    if (combined.length < 3) {
      setUpcLookup({
        activeSku: hit.sku_id,
        loading: false,
        items: [],
        error: null,
        message:
          "Enter a bit more in the name or subtitle before we can suggest a UPC.",
        openUpward,
      });
      return;
    }
    const cacheKey = makeUpcSearchCacheKey({
      title,
      subtitle,
      sku_id: hit.sku_id,
      product_id: hit.objectID,
      page: 1,
    });
    const cached = upcSearchCacheRef.current.get(cacheKey);
    if (cached) {
      setUpcLookup({
        activeSku: hit.sku_id,
        loading: false,
        items: cached.suggestions || [],
        retailerFeedUpcs: cached.retailer_feed_upcs || [],
        error: null,
        message: messageFromUpcSearchPayload(cached),
        openUpward,
      });
      return;
    }
    setUpcLookup({
      activeSku: hit.sku_id,
      loading: true,
      items: [],
      error: null,
      message: null,
      openUpward,
    });
    try {
      const data = await searchUpcDatabase({
        title,
        subtitle,
        sku_id: hit.sku_id,
        product_id: hit.objectID,
        page: 1,
      });
      if (data._debug) {
        console.warn("[MasterMenu UPC search] server _debug:", data._debug);
      }
      console.warn("[MasterMenu UPC search] response:", {
        upcdatabase_url: data._debug && data._debug.upcdatabase_url,
        success: data.success,
        suggestionCount: (data.suggestions && data.suggestions.length) || 0,
        message: data.message,
        query: data.query,
      });
      const payload = {
        suggestions: data.suggestions || [],
        retailer_feed_upcs: data.retailer_feed_upcs || [],
        success: data.success,
        message: data.message,
      };
      upcSearchCacheRef.current.set(cacheKey, payload);
      setUpcLookup({
        activeSku: hit.sku_id,
        loading: false,
        items: payload.suggestions,
        retailerFeedUpcs: payload.retailer_feed_upcs,
        error: null,
        message: messageFromUpcSearchPayload(payload),
        openUpward,
      });
    } catch (e) {
      const res = e.response;
      const body = res?.data || {};
      if (res?.status === 503 && body.error === "not_configured") {
        dispatch(
          addToast({
            type: "error",
            message:
              body.message ||
              "Automatic UPC suggestions are not set up. You can type the UPC yourself.",
            timer: 6,
          })
        );
      }
      const msg =
        body.message ||
        body.error ||
        e.message ||
        "UPC lookup failed";
      setUpcLookup({
        activeSku: hit.sku_id,
        loading: false,
        items: [],
        retailerFeedUpcs: [],
        error: msg,
        message: null,
        openUpward,
      });
    }
  };

  const saveUpc = async (skuId) => {
    const raw = drafts[skuId] != null ? drafts[skuId] : "";
    const upc_code = String(raw).trim();
    if (!upc_code) {
      dispatch(
        addToast({
          type: "error",
          message: "Enter a UPC before saving.",
          timer: 4,
        })
      );
      return;
    }
    setSavingSku(skuId);
    try {
      await patchAdminProductUpc(skuId, upc_code);
      dispatch(
        addToast({
          type: "success",
          message: `UPC saved for ${skuId}`,
          timer: 4,
        })
      );
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[skuId];
        return next;
      });
      setUpcLookup(initialLookup);
      refresh();
    } catch (e) {
      dispatch(
        addToast({
          type: "error",
          message: e.response?.data?.message || e.message || "Save failed",
          timer: 6,
        })
      );
    } finally {
      setSavingSku(null);
    }
  };

  if (status === "loading" || status === "stalled") {
    return (
      <table className="table-auto w-full dark:text-slate-300">
        <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
          <tr>
            <td>
              <div className="w-full text-center py-8">
                <Loader />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  return (
    <>
    <table className="table-auto w-full dark:text-slate-300">
      <thead className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20 border-t border-b border-slate-200 dark:border-slate-700">
        <tr>
          {headers.map((header, idx) => (
            <th
              key={"header_" + idx}
              className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap"
            >
              <div className="font-semibold text-left gap-2 flex">{header}</div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
        {hits.length === 0 ? (
          <tr>
            <td
              colSpan={headers.length}
              className="px-5 py-10 text-center text-slate-500 dark:text-slate-400 text-sm"
            >
              {missingUpcMode
                ? "No products missing a UPC match this search."
                : "No products match this search."}
            </td>
          </tr>
        ) : null}
        {hits.map((hit) => (
          <tr key={hit.objectID}>
            {/* Product */}
            <td className="px-2 first:pl-5 last:pr-5 py-3 w-80">
              <div className="flex items-center">
                <div className="w-10 h-10 shrink-0 mr-2 sm:mr-3 relative">
                  {hit.image_override || hit.image ? (
                    <img
                      className="rounded-full"
                      src={hit.image_override || hit.image}
                      width="40"
                      height="40"
                      alt={hit.title || ""}
                    />
                  ) : (
                    <div
                      className="w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-600"
                      aria-hidden
                    />
                  )}
                  {hit.image_override && (
                    <div
                      className="absolute -top-1 -right-1 bg-yellow-500 text-white text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center border-2 border-white dark:border-slate-800"
                      title="Image Override Active"
                    >
                      !
                    </div>
                  )}
                </div>
                <div className="text-slate-800 dark:text-slate-100 text-wrap">
                  <div className="font-medium">
                    {hit.title} {hit.subtitle}{" "}
                    {hit.volume_label || hit.volume
                      ? `- ${hit.volume_label || hit.volume}`
                      : ""}
                    {hit.sku_id?.startsWith("CXP-") && (
                      <span>
                        {" "}
                        <Badge color={"red"}>Custom</Badge>
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-400 mt-1">
                    <span className="mr-3">
                      <span className="font-semibold">SKU:</span>{" "}
                      {hit.sku_id || "-"}
                    </span>
                    {!missingUpcMode ? (
                      <span>
                        <span className="font-semibold">UPC:</span>{" "}
                        {hit.upc_code || "-"}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
            </td>

            {/* Brand */}
            <td className="px-2 first:pl-5 last:pr-5 py-3 w-36 text-wrap">
              <div className="text-left">{hit.company}</div>
            </td>

            {/* Categories */}
            <td className="px-2 first:pl-5 last:pr-5 py-3 w-36 text-wrap">
              <div className="flex items-center gap-2">
                <div className="text-left space-y-1">
                  {(() => {
                    let categories = hit.default_category;
                    if (!categories || categories.length === 0) {
                      categories = hit.base_category;
                    }
                    if (!categories) {
                      return null;
                    }
                    if (!Array.isArray(categories)) {
                      categories = [categories];
                    }
                    return categories.map((category, idx) => (
                      <Badge color={"purple"} key={"category_" + idx}>
                        <span
                          title={category}
                          className="max-w-[80px] truncate"
                        >
                          {category}
                        </span>
                      </Badge>
                    ));
                  })()}
                </div>
              </div>
            </td>

            {missingUpcMode ? (
              <td className="px-2 first:pl-5 last:pr-5 py-3 min-w-[14rem] align-top">
                <div className="relative flex flex-wrap items-start gap-2">
                  <div className="flex-1 min-w-[10rem] max-w-xs">
                    <input
                      className="form-input text-sm w-full"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      placeholder="Focus for suggestions or type UPC"
                      value={drafts[hit.sku_id] != null ? drafts[hit.sku_id] : ""}
                      onChange={(e) =>
                        setDrafts((prev) => ({
                          ...prev,
                          [hit.sku_id]: e.target.value,
                        }))
                      }
                      ref={(el) => {
                        inputRefsBySku.current[hit.sku_id] = el;
                      }}
                      onFocus={() => openUpcLookup(hit)}
                      onBlur={scheduleCloseLookup}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          saveUpc(hit.sku_id);
                        }
                      }}
                    />
                    {upcLookup.activeSku === hit.sku_id ? (
                      <div
                        className={`absolute left-0 right-0 z-30 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-theme-surface-accent shadow-lg max-h-56 overflow-y-auto text-left ${
                          upcLookup.openUpward
                            ? "bottom-full mb-1"
                            : "top-full mt-1"
                        }`}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        {upcLookup.loading ? (
                          <div className="px-3 py-4 flex justify-center">
                            <Loader />
                          </div>
                        ) : upcLookup.error ? (
                          <div className="px-3 py-2 text-xs text-red-600 dark:text-red-400">
                            {upcLookup.error}
                          </div>
                        ) : upcLookup.message &&
                          !upcLookup.items.length &&
                          !upcLookup.retailerFeedUpcs.length ? (
                          <div className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400">
                            {upcLookup.message}
                          </div>
                        ) : (
                          <div className="py-1">
                            <div className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                              Suggestions:
                            </div>
                            {upcLookup.items.length ? (
                              <ul>
                                {upcLookup.items.map((row, i) => (
                                  <li key={`${row.barcode}-${i}`}>
                                    <button
                                      type="button"
                                      className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 dark:hover:bg-slate-800/80 border-b border-slate-100 dark:border-slate-700 last:border-0"
                                      onClick={() => {
                                        clearBlurTimer();
                                        const code = String(row.barcode || "").trim();
                                        setDrafts((prev) => ({
                                          ...prev,
                                          [hit.sku_id]: code,
                                        }));
                                        setUpcLookup(initialLookup);
                                      }}
                                    >
                                      <div className="flex items-start gap-2">
                                        {row.image ? (
                                          <img
                                            src={row.image}
                                            alt={row.title || row.barcode || "UPC result"}
                                            className="w-10 h-10 rounded object-cover border border-slate-200 dark:border-slate-600 shrink-0"
                                          />
                                        ) : (
                                          <div className="w-10 h-10 rounded bg-slate-200 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 shrink-0 flex items-center justify-center text-[10px] text-slate-500 dark:text-slate-300">
                                            N/A
                                          </div>
                                        )}
                                        <div className="min-w-0">
                                          <div className="font-mono font-semibold text-slate-800 dark:text-slate-100">
                                            {row.barcode}
                                          </div>
                                          <div className="text-slate-700 dark:text-slate-200 mt-0.5 line-clamp-2">
                                            {row.title}
                                          </div>
                                          {row.description ? (
                                            <div className="text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-2">
                                              {row.description}
                                            </div>
                                          ) : null}
                                        </div>
                                      </div>
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <div className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400">
                                No suggestions found
                              </div>
                            )}
                            <div className="my-1 border-t border-slate-200 dark:border-slate-700" />
                            <div className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                              Other codes:
                            </div>
                            {upcLookup.retailerFeedUpcs.length ? (
                              <ul>
                                {upcLookup.retailerFeedUpcs.map((code) => (
                                  <li key={`feed-${code}`}>
                                    <button
                                      type="button"
                                      className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 dark:hover:bg-slate-800/80 border-b border-slate-100 dark:border-slate-700 last:border-0 font-mono font-semibold text-slate-800 dark:text-slate-100"
                                      onClick={() => {
                                        clearBlurTimer();
                                        setDrafts((prev) => ({
                                          ...prev,
                                          [hit.sku_id]: code,
                                        }));
                                        setUpcLookup(initialLookup);
                                      }}
                                    >
                                      {code}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <div className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400">
                                None here
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className="btn-xs bg-theme-button-primary text-theme-button-primary-text px-3 py-1 rounded text-xs font-medium disabled:opacity-50 shrink-0"
                    disabled={savingSku === hit.sku_id}
                    onClick={() => saveUpc(hit.sku_id)}
                  >
                    {savingSku === hit.sku_id ? "Saving…" : "Save"}
                  </button>
                </div>
              </td>
            ) : (
              <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap text-right">
                <button
                  type="button"
                  className="text-indigo-500 hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-300 text-sm font-medium"
                  onClick={() => {
                    setEditSku(hit.sku_id);
                    setEditOpen(true);
                  }}
                >
                  Edit
                </button>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
      <MasterMenuEditProduct
        sku={editSku}
        isOpen={editOpen}
        setIsOpen={setEditOpen}
        onSaved={() => refresh()}
      />
    </>
  );
};

export default MasterMenuHits;
